import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { ReactNode, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import InfiniteScrollSelect from "../../components/InfiniteScrollSelect";
import {
  BookingStatus,
  BookingStatusDisplayValue,
  BookingTypes,
  PaymentOptions,
} from "../../helpers/enums";
import { useAuth } from "../../hooks/useAuth";
import { useDebounce } from "../../hooks/useDebounce";
import { useQueryParam } from "../../hooks/useQueryParam";

const Filters = [
  { label: BookingStatusDisplayValue.ALL, key: BookingStatus.ALL },
  { label: BookingStatusDisplayValue.CONFIRMED, key: BookingStatus.CONFIRMED },
  { label: BookingStatusDisplayValue.PASSED, key: BookingStatus.PASSED },
  { label: BookingStatusDisplayValue.CANCELED, key: BookingStatus.CANCELED },
  { label: BookingStatusDisplayValue.REFUSED, key: BookingStatus.REFUSED },
  { label: BookingStatusDisplayValue.WAITING, key: BookingStatus.WAITING },
];

interface FormFields {
  search: string;
  place: string;
  SpecialOffers: string;
  type: string;
  paymentMethod: string;
}

interface Props {
  disableSearchInput?: boolean;
  disableFilters?: boolean;
  mode?: "all" | "user";
  userId?: string;
}

/** Label + control, with a consistent width so the row stays aligned */
function FilterField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div
      className="d-flex flex-column"
      style={{ minWidth: 180, flex: "1 1 180px", maxWidth: 260 }}
    >
      <label className="form-label text-sm fw-semibold mb-1">{label}</label>
      {children}
    </div>
  );
}

export default function BookingFilters({
  disableSearchInput = false,
  disableFilters = false,
  mode = "all",
}: Props) {
  const [searchParams] = useSearchParams();
  const { register, watch, setValue } = useForm<FormFields>();
  const { addMultiple, add } = useQueryParam();
  const search = useDebounce(watch("search"), 1000);
  const [selectedPlace, setSelectedPlace] = useState("");
  const [selectedOffer, setSelectedOffer] = useState("");
  const [selectedPartenaire, setSelectedPartenaire] = useState("");
  const [selectedVisiteReference, setSelectedVisiteReference] = useState("");

  const navigate = useNavigate();
  const tab = searchParams.get("tab") || "0";

  const { isAdmin, isMultiPlaceAdmin } = useAuth();

  useEffect(() => {
    setValue("search", searchParams.get("search") as string);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    addMultiple({
      search: search || "",
      tab,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, tab]);

  const resetStateFilters = () => {
    setValue("search", "");
    setValue("type", "");
    setSelectedPlace("");
    setSelectedOffer("");
    setSelectedPartenaire("");
    setSelectedVisiteReference("");

    const currentTab = searchParams.get("tab") || "0";
    navigate(`${window.location.pathname}?tab=${currentTab}`);
  };

  return (
    <nav className="d-flex flex-wrap align-items-end gap-3 px-3 pt-3 pb-3 border-bottom mb-4">
      {/* Status */}
      <FilterField label="Statut">
        <select
          className="form-select"
          value={searchParams.get("status") || BookingStatus.ALL}
          onChange={(e) => add("status", e.target.value)}
          disabled={disableFilters}
        >
          {Filters.map((filter) => (
            <option key={filter.key} value={filter.key}>
              {filter.label}
            </option>
          ))}
        </select>
      </FilterField>

      {isAdmin && (
        <>
          {/* Type */}
          <FilterField label="Type">
            <select
              className="form-select"
              value={searchParams.get("type") || ""}
              onChange={(e) => {
                add("type", e.target.value);
                setValue("type", e.target.value);
              }}
              disabled={disableFilters}
            >
              <option value="">Tous</option>
              <option value={BookingTypes.ACTIVITY_BOOKING}>Activités</option>
              <option value={BookingTypes.EVENT_BOOKING}>Événements</option>
              <option value={BookingTypes.PLACE_BOOKING}>Etablissements</option>
              <option value={BookingTypes.SPECIAL_OFFER_BOOKING}>Offres</option>
              <option value={BookingTypes.PACK_BOOKING}>Packs</option>
            </select>
          </FilterField>

          {/* Payment method */}
          <FilterField label="Méthode de paiement">
            <select
              className="form-select"
              value={searchParams.get("paymentMethod") || ""}
              onChange={(e) => {
                add("paymentMethod", e.target.value);
                setValue("paymentMethod", e.target.value);
              }}
              disabled={disableFilters}
            >
              <option value="">Tous</option>
              <option value={PaymentOptions.IN_PLACE}>En place</option>
              <option value={PaymentOptions.WAFACASH}>WafaCash</option>
              <option value={PaymentOptions.CREDIT_CARD}>
                Carte de crédit
              </option>
              <option value={PaymentOptions.STRIPE}>Stripe</option>
              <option value={PaymentOptions.USER_BALANCE}>Solde</option>
            </select>
          </FilterField>

          {/* Place */}
          <FilterField label="Lieu">
            <InfiniteScrollSelect
              key={"places"}
              isClearable={false}
              getOptionLabel={(option: Place) => option?.name}
              getOptionValue={(option: Place) => option.id}
              isOptionSelected={(option: Place) =>
                option.name === searchParams.get("place")
              }
              url={{ path: "places" }}
              value={selectedPlace}
              placeholder={selectedPlace.length > 0 ? selectedPlace : "Tous"}
              onChange={(place: Place) => {
                setSelectedPlace(place.name);
                add("place", place?.id);
              }}
            />
          </FilterField>

          {/* Special offer */}
          <FilterField label="Offre">
            <InfiniteScrollSelect
              key={"specials"}
              isClearable={false}
              getOptionLabel={(option: SpecialOffers) => option?.title ?? ""}
              getOptionValue={(option: SpecialOffers) => option.id}
              isOptionSelected={(option: SpecialOffers) =>
                option.title === searchParams.get("specials")
              }
              url={{ path: "specials" }}
              value={selectedOffer}
              placeholder={selectedOffer.length > 0 ? selectedOffer : "Toutes"}
              onChange={(offer: SpecialOffers) => {
                setSelectedOffer(offer.title);
                add("specialOffer", offer?.id);
              }}
            />
          </FilterField>
        </>
      )}

      {isMultiPlaceAdmin && (
        <>
          <FilterField label="Partenaire">
            <InfiniteScrollSelect
              key={"users"}
              isClearable={true}
              getOptionLabel={(option: PlaceManager) => option?.fullName ?? ""}
              getOptionValue={(option: PlaceManager) => option.id}
              isOptionSelected={(option: PlaceManager) =>
                option.id === searchParams.get("partenaire")
              }
              url={{ path: "users/place-manager" }}
              value={selectedPartenaire}
              placeholder={
                selectedPartenaire.length > 0 ? selectedPartenaire : "Tous"
              }
              onChange={(partner: PlaceManager) => {
                setSelectedPartenaire(partner.fullName);
                add("partenaire", partner?.id);
              }}
            />
          </FilterField>

          <FilterField label="Visite référence">
            <InfiniteScrollSelect
              key={"visiteReference"}
              isClearable={true}
              getOptionLabel={(option: PlaceManager) => option?.fullName ?? ""}
              getOptionValue={(option: PlaceManager) => option.id}
              isOptionSelected={(option: PlaceManager) =>
                option.id === searchParams.get("visiteReference")
              }
              url={{ path: "users/place-manager" }}
              value={selectedVisiteReference}
              placeholder={
                selectedVisiteReference.length > 0
                  ? selectedVisiteReference
                  : "Tous"
              }
              onChange={(partner: PlaceManager) => {
                setSelectedVisiteReference(partner.fullName);
                add("visiteReference", partner?.id);
              }}
            />
          </FilterField>
        </>
      )}

      {/* Search */}
      {!disableSearchInput && (
        <FilterField label="Recherche">
          <div
            className="search border rounded-sm px-2 d-flex align-items-center"
            style={{ height: 38 }}
          >
            <MagnifyingGlassIcon color="gray" width={20} />
            <input
              type="text"
              className="flex-grow-1 border-0 outline-none rounded-sm p-1 text-gray fs-6"
              placeholder="Rechercher"
              {...register("search")}
              readOnly={disableFilters}
              disabled={disableFilters}
            />
          </div>
        </FilterField>
      )}

      <button
        onClick={() => {
          resetStateFilters();
          if (mode === "all") {
            navigate("/bookings");
          }
        }}
        className="text-sm fw-semibold border text-red rounded-3 px-3 ms-auto"
        style={{ background: "none", height: 38 }}
        disabled={disableFilters}
      >
        Réinitialiser
      </button>
    </nav>
  );
}
